import React, { FC, useCallback, useRef } from 'react';
import { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

export interface EChartsContainerProps {
  option: EChartsOption;
  className?: string;
  style?: React.CSSProperties;
  error?: string | Error | null;
  loading?: boolean;
  notMerge?: boolean;
  onEvents?: Record<string, (params: unknown) => void>;
  onChartReady?: (chart?: unknown) => void;
}

const defaultChartStyle = { height: '400px', width: '100%' };

export const EChartsContainer: FC<EChartsContainerProps> = ({
  option,
  className,
  style,
  error = null,
  loading = false,
  notMerge = false,
  onEvents,
  onChartReady,
}) => {
  const observerRef = useRef<ResizeObserver | null>(null);

  const handleChartReady = useCallback((chart: any) => {
    // Observe the chart's DOM container so that any layout shift (sidebar toggle,
    // scroll container reflow, etc.) causes ECharts to recalculate its bounding rect.
    // Without this, the crosshair/tooltip coordinate mapping gets a stale left offset.
    const container = chart.getDom?.() as HTMLElement | undefined;
    if (container) {
      observerRef.current?.disconnect();
      const observer = new ResizeObserver(() => chart.resize());
      observer.observe(container);
      observerRef.current = observer;
    }
    onChartReady?.(chart);
  }, [onChartReady]);

  if (error) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          color: '#d32f2f',
          ...style,
        }}>
        <div>
          <strong>Error loading chart:</strong> {error instanceof Error ? error.message : String(error)}
        </div>
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      notMerge={notMerge}
      style={style ?? defaultChartStyle}
      className={className}
      onEvents={onEvents}
      showLoading={loading}
      onChartReady={handleChartReady}
      opts={{ renderer: 'svg' }}
    />
  );
};
